import { QueryAdminDashboardResponse } from "../../../../types";
import { AlertTitle, Typography as T } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import Alert from "@mui/material/Alert";
import { colors } from "../../../../styles";
import { StatBox, StatValue } from "../components";
import { useStoreState, useStoreActions } from "../../../../store";
import ReportingService from "../../../../api/services/ReportingService";
import { ReactComponent as DollarBillIcon } from "../../../../assets/icons/adminDashboard/dollarbill.svg";
import { ReactComponent as PersonPlusIcon } from "../../../../assets/icons/adminDashboard/personPlus.svg";
import { ReactComponent as CircleGraphIcon } from "../../../../assets/icons/adminDashboard/circleGraph.svg";
import { ReactComponent as TwoPersonIcon } from "../../../../assets/icons/adminDashboard/twoPerson.svg";
import { ReactComponent as PieChartIcon } from "../../../../assets/icons/adminDashboard/piechart.svg";
import { ReactComponent as BankIcon } from "../../../../assets/icons/adminDashboard/bank.svg";
import { ReactComponent as BarChartIcon } from "../../../../assets/icons/adminDashboard/barchart.svg";

const ProgramLive = ({ data }: { data: QueryAdminDashboardResponse }) => {
  const dashboardState = useStoreState((state) => state.dashboard);
  const dashboardActions = useStoreActions((actions) => actions.dashboard);

  const handleRunReport = async () => {
    await ReportingService.generateResultsSummaryReport({});
  };

  const chartData = () => {
    return (
      data.stats?.participants_count_over_time?.map((item, index) => {
        let label = "Now";
        if (index === 0) {
          label = "2 Months";
        } else if (index === 1) {
          label = "1 Month";
        }
        return {
          participants: item,
          month: label,
        };
      }) || []
    );
  };

  return (
    <div className="flex flex-col w-100 mb-64">
      <div
        className="w-100 flex flex-col"
        style={{ justifyContent: "flex-end" }}
      >
        <T variant="lgReg" alignSelf={"flex-end"}>
          Share With Your Team
        </T>
        <T
          variant="smMed"
          alignSelf={"flex-end"}
          style={{ color: colors.info_blue, cursor: "pointer" }}
          onClick={() => handleRunReport()}
        >
          Export ESA Results Report
        </T>
      </div>
      <div className="flex flex-col">
        {dashboardState.dismissedProgramIsLiveAlert ? null : (
          <Alert
            sx={{ marginTop: 1, marginBottom: 3 }}
            onClose={() =>
              dashboardActions.setDismissedProgramIsAliveAlert(true)
            }
          >
            <AlertTitle>Your Emergency Savings Program is Live</AlertTitle>
            <T variant="mdMed" className="w-50">
              Eligible employees have been invited to enroll. You can track the
              program here in the dashboard.
            </T>
          </Alert>
        )}
        <T variant="headingSm" fontWeight="bold" className="w-50">
          Program
        </T>
        <div className="flex flex-row gap-16">
          <StatBox Icon={DollarBillIcon} title="Total Saved by Employees">
            <StatValue
              value={`$${
                data.stats?.total_savings
                  ? Math.round(data.stats.total_savings).toLocaleString()
                  : 0
              }`}
            />
          </StatBox>
          <StatBox
            Icon={BarChartIcon}
            title="Projected Savings This Year"
            description="The cumulative amount that employees will save based on current contribution amounts."
          >
            <StatValue
              value={`$${Math.round(
                data.stats?.annualized_savings || 0
              ).toLocaleString()}`}
            />
          </StatBox>
          <StatBox Icon={PersonPlusIcon} title="Sign-ups Last 30 Days">
            <StatValue
              value={`${Math.round(
                data.stats?.sign_ups_last_30_days || 0
              ).toLocaleString()}`}
            />
          </StatBox>
          <StatBox
            Icon={CircleGraphIcon}
            title="% Received Match Last 30 Days"
            description="The percentage of participants who received a match in the last 30 days."
          >
            <StatValue
              value={`${
                data.stats?.percent_receiving_bonus_in_last_30_days
                  ? Math.round(
                      data.stats?.percent_receiving_bonus_in_last_30_days * 100
                    )
                  : 0
              }%`}
            />
          </StatBox>
        </div>
        <div className="flex flex-row gap-16">
          <div className="flex flex-2" style={{ height: 370 }}>
            <StatBox Icon={TwoPersonIcon} title="Participating Employees">
              <div className="mb-16">
                <StatValue value={data.stats?.total_participants || 0} />
              </div>
              {chartData().length ? (
                <ResponsiveContainer height={180} width="95%">
                  <LineChart data={chartData() || []} margin={{ left: -20 }}>
                    <Line
                      type="monotone"
                      dataKey="participants"
                      stroke="#FFBE5F"
                      activeDot={{ r: 8 }}
                    />
                    <CartesianGrid stroke="#ECECEC" strokeDasharray="3 3" />
                    <YAxis dataKey="participants" axisLine={false} />
                    <XAxis dataKey="month" axisLine={false} />
                    <Tooltip />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info">
                  <T variant="smReg">
                    Participant chart will show once you have active
                    participants.
                  </T>
                </Alert>
              )}
            </StatBox>
          </div>
        </div>
        <div className="mt-32">
          <T variant="headingSm" fontWeight="bold" className="w-50">
            Employee
          </T>
          <div className="flex flex-row gap-16">
            <div className="flex flex-1">
              <StatBox Icon={PieChartIcon} title="Average Savings Balance">
                <StatValue
                  value={`$${
                    data.stats?.average_savings_balance
                      ? Math.round(
                          data.stats.average_savings_balance
                        ).toLocaleString()
                      : 0
                  }`}
                />
              </StatBox>
            </div>
            <div className="flex flex-1">
              <StatBox Icon={BankIcon} title="Average Monthly Contribution">
                <StatValue
                  value={`$${
                    data.stats?.average_monthly_contribution
                      ? Math.round(
                          data.stats.average_monthly_contribution
                        ).toLocaleString()
                      : 0
                  }`}
                />
              </StatBox>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramLive;
